import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { NamedLink } from '../../components';
import Field from '../PageBuilder/Field';

import css from './InvestorHomepageSections.module.css';
import heroCss from './HeroBannerOverrides.module.css';
import founderPhoto from './FounderPhoto';

const tractionItems = [
  { value: 'Live', label: 'Booking requests are active' },
  { value: 'Sydney', label: 'Focused first launch market' },
  { value: '10%', label: 'Commission on completed bookings' },
  { value: 'Growing', label: 'Artist network expanding' },
];

const trustSignals = [
  {
    title: 'Clear artist pricing',
    description: 'Clients can compare portfolios and prices before sending a request.',
  },
  {
    title: 'Payment after acceptance',
    description: 'A client is only charged when the artist confirms the booking.',
  },
  {
    title: 'Studio and mobile options',
    description: 'Artists can clearly show where and how they provide their services.',
  },
  {
    title: 'Booking support built in',
    description: 'Requests, appointment details and communication stay together in one place.',
  },
];

export const InvestorHeroSection = props => {
  const { sectionId, className, appearance, options } = props;
  const hasBackgroundImage = Boolean(appearance?.backgroundImage);
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const currentUser = useSelector(state => state.user.currentUser);
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isArtist = userType === 'provider';
  const showArtistCta = !isAuthenticated || isArtist;
  const artistCtaName = isArtist ? 'NewListingPage' : 'SignupForUserTypePage';
  const artistCtaParams = isAuthenticated ? undefined : { userType: 'provider' };
  const artistCtaLabel = isArtist ? 'Post a listing' : 'Join as an artist';

  return (
    <section
      id={sectionId}
      className={classNames(css.hero, className, {
        [heroCss.heroWithImage]: hasBackgroundImage,
      })}
    >
      {appearance?.fieldType === 'customAppearance' ? (
        <Field
          data={{ ...appearance, alt: 'Hotline Glam makeup looks collage' }}
          className={heroCss.heroBackground}
          options={options}
        />
      ) : null}
      <div className={css.heroGlow} aria-hidden="true" />
      <div className={classNames(css.heroInner, heroCss.heroContent)}>
        <div className={classNames(css.heroCopy, heroCss.heroCopy)}>
          <span className={classNames(css.eyebrow, heroCss.eyebrow)}>
            Sydney’s last-minute glam marketplace
          </span>
          <h1 className={classNames(css.heroTitle, heroCss.heroTitle)}>
            Makeup emergency? We’ve got you.
          </h1>
          <p className={classNames(css.heroDescription, heroCss.heroDescription)}>
            Find Sydney makeup artists in one place—making last-minute glam easier to organise.
          </p>

          <div className={css.heroActions}>
            <NamedLink
              name="SearchPage"
              className={classNames(css.primaryButton, heroCss.primaryButton)}
            >
              Find an artist
            </NamedLink>
            {showArtistCta ? (
              <NamedLink
                name={artistCtaName}
                params={artistCtaParams}
                className={classNames(css.secondaryButton, heroCss.secondaryButton)}
              >
                {artistCtaLabel}
              </NamedLink>
            ) : null}
          </div>

          <div
            className={classNames(css.heroProof, heroCss.heroProof)}
            aria-label="Hotline Glam benefits"
          >
            <span>Made for last-minute glam</span>
            <span>Mobile and studio artists</span>
            <span>Compare artists in one place</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export const TractionSection = props => {
  const { sectionId } = props;

  return (
    <section id={sectionId} className={css.tractionSection}>
      <div className={css.sectionInner}>
        <div className={css.sectionHeadingRow}>
          <div className={css.tractionHeading}>
            <h2 className={css.sectionTitle}>A focused launch with a scalable marketplace model.</h2>
          </div>
          <p className={css.sectionDescription}>
            Hotline Glam launched publicly in July 2026, starting with Sydney and a simple model
            that grows as more artists and clients join.
          </p>
        </div>

        <div className={css.tractionGrid}>
          {tractionItems.map(item => (
            <div className={css.tractionCard} key={item.value}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const FounderSection = props => {
  const { sectionId, appearance, options } = props;

  return (
    <section id={sectionId} className={css.founderSection}>
      {appearance?.fieldType === 'customAppearance' ? (
        <Field
          data={{ ...appearance, alt: 'Hotline Glam makeup looks collage' }}
          className={css.founderBackground}
          options={options}
        />
      ) : null}
      <div className={css.founderInner}>
        <div className={css.founderMark}>
          <img
            className={css.founderPhoto}
            src={founderPhoto}
            alt="Chloe Rophael, founder of Hotline Glam"
          />
        </div>

        <div className={css.founderCopy}>
          <span className={css.eyebrow}>Founder-market fit</span>
          <h2 className={css.sectionTitle}>Built by the makeup artist who lived the problem.</h2>
          <p className={css.founderLead}>
            Hotline Glam was founded by Chloe Rophael, a Sydney makeup artist with almost seven years of professional industry experience.
          </p>
          <p className={css.founderBody}>
            After seeing how difficult it could be for clients to find the right artist—and how
            often talented artists had unfilled appointments—she created one platform designed to
            solve both sides of the booking problem.
          </p>
          <div className={css.founderPoints}>
            <span>Built from real booking pain points</span>
            <span>Designed to expand city by city</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export const TrustSection = props => {
  const { sectionId, appearance, options } = props;

  return (
    <section id={sectionId} className={css.trustSection}>
      <div className={css.sectionInner}>
        <div className={css.finalCta}>
          {appearance?.fieldType === 'customAppearance' ? (
            <Field
              data={{ ...appearance, alt: 'Hotline Glam makeup looks collage' }}
              className={css.finalCtaBackground}
              options={options}
            />
          ) : null}
          <div>
            <span className={css.eyebrow}>Hotline Glam</span>
            <h2>Ready to find your makeup artist?</h2>
          </div>
          <div className={css.finalCtaActions}>
            <NamedLink name="SearchPage" className={css.primaryButton}>
              Browse Sydney artists
            </NamedLink>
            <NamedLink name="HowItWorksPage" className={css.secondaryButton}>
              How it works
            </NamedLink>
            <NamedLink name="CMSPage" params={{ pageId: 'faqs' }} className={css.secondaryButton}>
              FAQs
            </NamedLink>
            <NamedLink name="AboutPage" className={css.secondaryButton}>
              About us
            </NamedLink>
          </div>
        </div>
      </div>
    </section>
  );
};
